/**
 * Whether the user is currently driving the app with a pointer or a keyboard.
 *
 * Exists for one reason: WebKit applies `:focus-visible` when focus is moved
 * PROGRAMMATICALLY, not only when the user tabbed there. Radix restores focus
 * to the trigger when a dialog closes, so on iOS a customer who taps the close
 * button, then cancels the "leave the booking?" dialog, is returned to a button
 * wearing a 2px primary-coloured focus ring — a teal circle around the ✕ that
 * they never asked for and cannot dismiss. Reported on the KGSt-Forum
 * walkthrough (PO, 2026-09-15).
 *
 * The ring itself is correct and stays: it is how a keyboard user knows where
 * they are (WCAG 2.4.7). What has to go is showing it to someone who never used
 * a keyboard — so the modality has to be known, and the platform will not tell
 * us.
 *
 * The modality is consumed two ways:
 *  - `getInputModality()` for behavioral decisions (the dialog's focus-restore
 *    suppression in `components/ui/dialog.tsx`);
 *  - a `data-input-modality` attribute mirrored onto `<html>`, which
 *    `index.css` uses to suppress the VISUAL focus ring app-wide while the
 *    modality is `pointer` (PO sweep decision, 2026-09-16 — the per-button
 *    onFocus/blur guard this replaces would have needed re-adding on every
 *    close button forever). Purely cosmetic: nothing is blurred and no focus
 *    behavior changes, so Radix focus management is untouched.
 *
 * Listeners are attached once, at module load, in capture phase so nothing can
 * stop them; they are passive and do no work beyond assigning a string and an
 * attribute.
 */
export type InputModality = "pointer" | "keyboard";

let modality: InputModality = "pointer";

const applyModality = (next: InputModality): void => {
    modality = next;
    if (typeof document !== "undefined") {
        document.documentElement.setAttribute("data-input-modality", next);
    }
};

if (typeof window !== "undefined") {
    // Written eagerly so the CSS attribute selector matches from the first
    // paint — an absent attribute would leave the pre-interaction state
    // (which on a phone is exactly when programmatic focus can land) unstyled.
    applyModality(modality);

    window.addEventListener(
        "pointerdown",
        () => {
            applyModality("pointer");
        },
        { capture: true, passive: true }
    );
    window.addEventListener(
        "keydown",
        (event: KeyboardEvent) => {
            // Modifier presses alone are not navigation — holding Shift to type
            // a capital letter must not flip a touch user into keyboard mode.
            if (event.metaKey || event.altKey || event.ctrlKey) return;
            applyModality("keyboard");
        },
        { capture: true, passive: true }
    );
}

export const getInputModality = (): InputModality => modality;

/** Test seam — the listeners above are module-level and cannot be re-run. */
export const setInputModalityForTest = (next: InputModality): void => {
    applyModality(next);
};
